import { rootApi } from '../../../store/rootApi.js'
import type {
  Enrolment,
  MfaChallenge,
  MfaResetConfirmBody,
  MfaResetRequestBody,
  MfaResetRequested,
  Permissions,
  ResetLinkState,
  Session,
  SignInResult,
} from '../core/types.js'

/**
 * 1.10 — signing in to the desk, which is served from the **app** surface.
 *
 * Every other module here talks to `/v1/admin`. Authentication does not: there
 * is one identity in the platform and one place it is proved, and the
 * administrator's second step lives beside the customer's sign-in rather than
 * in a second implementation of the same thing.
 *
 * Every write here invalidates `Permissions`, and that is not bookkeeping to
 * satisfy a rule. Each one changes **who is asking**, and `GET /me/permissions`
 * is the one cached answer that depends on that: signing in makes it readable,
 * finishing the second step makes it true, signing out makes it a 401. The
 * rest of the cache is not invalidated but discarded outright when a session
 * ends, which `App` does — a stale table belonging to the last person is worse
 * than no table.
 */
const SESSION_CHANGED = [{ type: 'Permissions' as const, id: 'ME' }]
export const signInApi = rootApi.injectEndpoints({
  endpoints: (build) => ({
    signIn: build.mutation<SignInResult, { identifier: string; password: string }>({
      query: (body) => ({ url: '/v1/app/auth/sign-in', method: 'POST', data: body }),
      invalidatesTags: SESSION_CHANGED,
    }),

    /** The second step: six digits from an app, or one of the recovery codes. */
    verifyCode: build.mutation<Session, { challenge: string; code: string }>({
      query: (body) => ({ url: '/v1/app/auth/admin/mfa/verify', method: 'POST', data: body }),
      invalidatesTags: SESSION_CHANGED,
    }),

    /** The first time only: the secret, the QR, and the codes shown once. */
    startEnrolment: build.mutation<Enrolment, { challenge: string }>({
      query: (body) => ({ url: '/v1/app/auth/admin/mfa/enrol', method: 'POST', data: body }),
      invalidatesTags: SESSION_CHANGED,
    }),

    /** Proving the app is really set up, which also finishes signing in. */
    confirmEnrolment: build.mutation<Session, { challenge: string; code: string }>({
      query: (body) => ({ url: '/v1/app/auth/admin/mfa/confirm', method: 'POST', data: body }),
      invalidatesTags: SESSION_CHANGED,
    }),

    /**
     * "Lost your authenticator" — email a reset code to the address on the account.
     *
     * Answers the same either way, so nothing here reveals whether an account has a
     * factor. Nothing to invalidate: no session changes until the reset is confirmed.
     */
    requestMfaReset: build.mutation<MfaResetRequested, MfaResetRequestBody>({
      query: (body) => ({ url: '/v1/app/auth/admin/mfa/reset/request', method: 'POST', data: body }),
    }),

    /**
     * The reset code is right: the old factor is cleared and a fresh enrolment comes
     * back, so the screen goes straight to a new QR and new recovery codes.
     */
    confirmMfaReset: build.mutation<Enrolment, MfaResetConfirmBody>({
      query: (body) => ({ url: '/v1/app/auth/admin/mfa/reset/confirm', method: 'POST', data: body }),
    }),

    /**
     * The session that survives a reload.
     *
     * The refresh token is in an httpOnly cookie the browser holds and script
     * cannot read, so this request carries nothing and the server answers from
     * the cookie. It is why the desk does not sign you out every time you press
     * F5, and it is the one call made before anything is drawn.
     */
    restore: build.mutation<Session, void>({
      query: () => ({ url: '/v1/app/auth/refresh', method: 'POST', data: {} }),
      invalidatesTags: SESSION_CHANGED,
    }),

    /**
     * 1.8 — a reset link, and the same answer whether or not the address exists.
     *
     * 1.10 offers **Reset your password** as one of its two ways out and the
     * desk had neither the control nor the two screens behind it. The link
     * now opens the desk rather than the app, because an administrator's
     * password is used here: the reset email reads the account's roles and
     * builds the address from that.
     */
    forgotPassword: build.mutation<unknown, { email: string }>({
      query: (body) => ({ url: '/v1/app/auth/forgot-password', method: 'POST', data: body }),
      invalidatesTags: SESSION_CHANGED,
    }),

    /**
     * 1.11 — whether the link still works, read the moment it opens rather than
     * discovered by trying it. The phone screen does the same thing (`checkResetLink`,
     * `ResetPassword.tsx`); the desk never read it at all, so a spent or hour-old link
     * still drew a live-looking form, and only the click at the end said otherwise —
     * a member found this by using a link twice and being unsure the second time
     * whether anything had happened.
     */
    checkResetLink: build.query<ResetLinkState, { token: string }>({
      query: (body) => ({ url: '/v1/app/auth/reset-password/check', method: 'POST', data: body }),
      providesTags: ['ResetLink'],
      keepUnusedDataFor: 0,
    }),

    /** 1.11 — the link's own screen. Saving signs them in on this device. */
    resetPassword: build.mutation<Session, { token: string; password: string }>({
      query: (body) => ({ url: '/v1/app/auth/reset-password', method: 'POST', data: body }),
      // Not 'ResetLink': the link is spent the moment this succeeds, and re-reading it
      // would show 1.12 (expired) under the person on their way to the next step.
      invalidatesTags: SESSION_CHANGED,
    }),

    signOut: build.mutation<unknown, void>({
      query: () => ({ url: '/v1/app/auth/sign-out', method: 'POST', data: {} }),
      invalidatesTags: SESSION_CHANGED,
    }),

    /**
     * What this administrator may do, and who could do the rest.
     *
     * Read once a session exists and kept in the session slice, because every
     * rail item and every button on every screen asks it. `roles_with` is what
     * lets 9.9 say *"ask an Operations colleague"* with the real role name
     * rather than a word typed into the screen.
     */
    getPermissions: build.query<Permissions, void>({
      query: () => ({ url: '/v1/admin/me/permissions', method: 'GET' }),
      providesTags: [{ type: 'Permissions', id: 'ME' }],
    }),
  }),
})

/** Narrowing the two shapes sign-in can answer with, in one place. */
export const isChallenge = (r: SignInResult): r is MfaChallenge =>
  (r as MfaChallenge).mfa_required === true

export const {
  useSignInMutation,
  useVerifyCodeMutation,
  useStartEnrolmentMutation,
  useConfirmEnrolmentMutation,
  useRequestMfaResetMutation,
  useConfirmMfaResetMutation,
  useRestoreMutation,
  useForgotPasswordMutation,
  useCheckResetLinkQuery,
  useResetPasswordMutation,
  useSignOutMutation,
  useGetPermissionsQuery,
} = signInApi
